import { supabase } from './supabase';
import type { Platform } from './types';

export const fetchPlatformMetrics = async (platformId: string, days: number = 30) => {
  try {
    console.log(`Fetching metrics for platform: ${platformId}`);
    
    // Get user's ID from session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      throw new Error('User not authenticated');
    }

    // Get platform connection and credentials
    const { data: connection, error: connectionError } = await supabase
      .from('platform_connections')
      .select('*')
      .eq('platform_id', platformId)
      .eq('user_id', session.user.id)
      .eq('is_active', true)
      .single();

    if (connectionError) {
      console.error('Connection error:', connectionError);
      throw connectionError;
    }

    if (!connection) {
      console.error('No active connection found for platform:', platformId);
      throw new Error('Platform not connected');
    }

    console.log(`Found connection for ${platformId}:`, connection.id);

    // Calculate date range
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days);

    // Get metrics from database
    const { data: metrics, error: metricsError } = await supabase
      .from('platform_metrics')
      .select('*')
      .eq('platform_id', platformId)
      .eq('user_id', session.user.id)
      .gte('date', startDate.toISOString().split('T')[0])
      .lte('date', endDate.toISOString().split('T')[0])
      .order('date', { ascending: false });

    if (metricsError) {
      console.error('Metrics error:', metricsError);
      throw metricsError;
    }

    console.log(`Retrieved ${metrics?.length || 0} metrics for ${platformId}`);
    return metrics || [];
  } catch (error) {
    console.error('Error fetching platform metrics:', error);
    throw error;
  }
};

export const aggregateMetrics = (metrics: any[], platform: Platform) => {
  if (!metrics || metrics.length === 0) {
    console.log('No metrics to aggregate');
    return platform.metrics.map(metric => ({
      key: metric.key,
      label: metric.label,
      format: metric.format,
      value: 0,
      change: 0,
    }));
  }

  // Calculate totals for the current period
  const totalMetrics = metrics.reduce((acc, item) => {
    const dayMetrics = item.metrics || {};
    Object.keys(dayMetrics).forEach(key => {
      acc[key] = (acc[key] || 0) + (dayMetrics[key] || 0);
    });
    return acc;
  }, {});

  // Calculate averages for rate metrics
  const rateMetrics = ['ctr', 'conversion_rate', 'engagement_rate'];
  rateMetrics.forEach(key => {
    if (key in totalMetrics) {
      totalMetrics[key] = totalMetrics[key] / metrics.length;
    }
  });

  // Calculate period-over-period change
  const midPoint = Math.floor(metrics.length / 2);
  const currentPeriod = metrics.slice(0, midPoint);
  const previousPeriod = metrics.slice(midPoint);

  const calculatePeriodMetrics = (periodData: any[]) => {
    return periodData.reduce((acc, item) => {
      const dayMetrics = item.metrics || {};
      Object.keys(dayMetrics).forEach(key => {
        acc[key] = (acc[key] || 0) + (dayMetrics[key] || 0);
      });
      return acc;
    }, {});
  };

  const currentMetrics = calculatePeriodMetrics(currentPeriod);
  const previousMetrics = calculatePeriodMetrics(previousPeriod);

  // Calculate changes
  return platform.metrics.map(metric => {
    const currentValue = currentMetrics[metric.key] || 0;
    const previousValue = previousMetrics[metric.key] || 0;
    const change = previousValue === 0 ? 0 : 
      ((currentValue - previousValue) / previousValue) * 100;

    // For rate metrics, use the average
    const value = rateMetrics.includes(metric.key) ?
      totalMetrics[metric.key] || 0 :
      totalMetrics[metric.key] || 0;

    return {
      key: metric.key,
      label: metric.label,
      format: metric.format,
      value,
      change: Number(change.toFixed(2)),
    };
  });
};

const calculateChange = (current: number, previous: number): number => {
  if (!previous) return 0;
  return Number(((current - previous) / previous * 100).toFixed(2));
};